import React, { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  Search,
  Phone,
  ChevronRight,
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
} from "lucide-react";
import { HERO, HERO_SERVICES, SERVICES } from "../mock";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const ICONS = {
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
};

const ROTATE_MS = 3000;

const WhatsAppIcon = ({ size = 18, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.945c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.36 11.949-11.945a11.821 11.821 0 00-3.481-8.418" />
  </svg>
);

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [pick, setPick] = useState("");
  const navigate = useNavigate();
  const timer = useRef(null);

  const findAndBook = () => {
    if (pick) navigate(`/services/${pick}`);
    else window.location.href = "/#book";
  };

  const start = () => {
    stop();
    timer.current = setInterval(
      () => setIndex((i) => (i + 1) % HERO_SERVICES.length),
      ROTATE_MS
    );
  };
  const stop = () => timer.current && clearInterval(timer.current);

  useEffect(() => {
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goto = (i) => {
    setIndex(i);
    start();
  };
  const next = () => goto((index + 1) % HERO_SERVICES.length);

  const active = HERO_SERVICES[index];
  const Icon = ICONS[active.icon] || Stethoscope;

  return (
    <section
      id="home"
      className="relative overflow-hidden pt-[118px] pb-16 md:pb-24"
      style={{
        background:
          "radial-gradient(1100px 560px at 88% 0%, hsl(var(--primary)/0.10) 0%, hsl(var(--primary)/0) 55%), hsl(var(--background))",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[minmax(0,44%)_minmax(0,56%)] gap-10 lg:gap-12 items-center">
        {/* LEFT — static */}
        <div>
          <div className="inline-flex items-center gap-2 bg-white/80 border border-border rounded-full pl-2 pr-4 py-1.5 shadow-sm">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/15">
              <ShieldCheck size={14} className="text-primary" />
            </span>
            <span className="text-[11px] font-bold tracking-[0.13em] text-foreground/70 uppercase">
              {HERO.badge}
            </span>
          </div>

          <h1 className="font-heading font-extrabold text-foreground mt-6 leading-[1.05] text-[32px] md:text-[58px] tracking-tight">
            Expert medical care,
            <br />
            <span className="text-primary">delivered</span> at home.
          </h1>

          <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/[0.06] px-5 py-4 max-w-xl" data-testid="hero-service-types">
            <p className="text-[14.5px] md:text-[15px] font-semibold text-foreground leading-snug">
              Service types start from 2–4 hrs, 8–10 hrs, 24 hrs to daily care services.
            </p>
            <p className="text-[13.5px] md:text-[14px] text-muted-foreground mt-1.5">
              Short visits available for special handling.
            </p>
          </div>


          <p className="text-muted-foreground text-[16px] md:text-[17px] leading-relaxed mt-6 max-w-xl">
            From nursing and elder care to post-operative recovery — Healthsqur
            brings trusted, trained professionals to your doorstep.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-8">
            <a
              href="https://wa.me/919611761212?text=Hi%20Healthsqur%2C%20I'd%20like%20to%20book%20a%20caregiver."
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden flex items-center gap-2 px-7 py-3.5 rounded-full bg-foreground text-background font-semibold hover:opacity-90 transition-opacity shadow-[0_10px_30px_hsl(var(--foreground)/0.28)]"
            >
              <WhatsAppIcon size={18} className="text-background" /> Connect on WhatsApp
            </a>
            <a
              href="#book"
              className="hidden md:flex items-center gap-2 px-7 py-3.5 rounded-full bg-foreground text-background font-semibold hover:opacity-90 transition-opacity shadow-[0_10px_30px_hsl(var(--foreground)/0.28)]"
            >
              <Phone size={18} /> Book a Caregiver
            </a>
            <a
              href="#services"
              className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-border text-foreground font-semibold hover:border-primary hover:text-primary transition-colors"
            >
              <Search size={18} /> Explore Services
            </a>
          </div>

          {/* Mobile: find & book a service tab */}
          <div className="md:hidden mt-7 bg-white rounded-[22px] border border-border shadow-[0_14px_40px_rgba(0,0,0,0.08)] p-4">
            <p className="text-[13px] font-semibold text-foreground mb-2">What care do you need?</p>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="w-full bg-[#F1F4F8] border-border rounded-xl px-4 py-6 text-[14px]">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={findAndBook}
              className="w-full mt-3 flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-semibold shadow-[0_10px_30px_hsl(var(--primary)/0.35)]"
            >
              <Search size={18} /> Book Now
            </button>
            <a
              href="tel:+919611761212"
              className="flex items-center justify-center gap-2 mt-3 text-[14px] font-semibold text-foreground"
            >
              <Phone size={15} className="text-primary" /> Call us: +91 96117 61212
            </a>
          </div>
        </div>
        <div
          className="relative"
          onMouseEnter={stop}
          onMouseLeave={start}
        >
          <div className="bg-white rounded-[24px] shadow-[0_24px_60px_rgba(0,0,0,0.12)] border border-border p-3 md:p-4 float-slow">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              {/* Image */}
              <div className="md:flex-[1.35] rounded-[18px] overflow-hidden h-[240px] md:h-[380px]">
                <img
                  key={`img-${active.key}`}
                  src={active.img}
                  alt={active.title}
                  className="w-full h-full object-cover hero-in hero-zoom"
                />
              </div>

              {/* Text */}
              <div
                key={`txt-${active.key}`}
                className="md:flex-1 flex flex-col justify-center px-3 md:px-5 py-4 hero-text-in"
              >
                <span className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-5">
                  <Icon size={26} className="text-primary" />
                </span>
                <h3 className="font-heading font-extrabold text-foreground text-[26px] md:text-[30px] leading-tight">
                  {active.title}
                </h3>
                <span className="block w-12 h-[3px] rounded-full bg-primary my-4" />
                <p className="text-muted-foreground text-[15px] md:text-[16px] leading-relaxed max-w-[320px]">
                  {active.desc}
                </p>
              </div>
            </div>

            {/* Progress bars (fill then advance) */}
            <div className="flex items-center justify-center gap-2 pt-4 pb-1">
              {HERO_SERVICES.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => goto(i)}
                  aria-label={`Show ${s.title}`}
                  className="h-[4px] w-8 md:w-9 rounded-full bg-foreground/15 overflow-hidden"
                >
                  {i < index && <span className="block h-full w-full bg-primary" />}
                  {i === index && (
                    <span
                      key={`fill-${active.key}`}
                      className="block h-full bg-primary bar-fill"
                      style={{ animationDuration: `${ROTATE_MS}ms` }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Next arrow */}
          <button
            onClick={next}
            aria-label="Next service"
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 items-center justify-center w-11 h-11 rounded-full bg-foreground text-background shadow-[0_10px_24px_hsl(var(--foreground)/0.3)] hover:opacity-90 transition-opacity"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
